import React from 'react'
import { subMonths, startOfMonth, endOfMonth } from 'date-fns'
import { Button, Card, Element, Heading, Icon, Level, Loader } from 'react-bulma-components'
import { EditIcon } from '../icons'
import type { WeekSummary } from '../../../../backend/src/services/resources/missions/missions.summary.service.types'
import type { SummaryDayData } from '../../../../backend/src/services/resources/period-summaries/period-summary.types'
import { Mission } from '../../api/missions'
import { WorkPeriodWithSummary } from '../../api/work-periods'
import WeekRangeFilter from '../filters/fields/week-range'
import DailyWorkPeriodsTable from '../work-periods/daily-work-periods-table'
import { InlineWorkPeriodsForm } from '../work-periods/forms/inline-work-periods-form'
import Protected from '../protected/protected'
import { IsoDate } from '../../hooks/use-calendar'
import { dateToDateInputValue, endOfWeek, localDate, startOfWeek } from '../../utils/date'

export interface MissionWorkPeriodsPanelProps {
  mission: Mission
  weekRangeFromIso: IsoDate | ''
  weekRangeToIso: IsoDate | ''
  onWeekRangeFromChange: (value: IsoDate | '') => void
  onWeekRangeToChange: (value: IsoDate | '') => void
  summaryFromTo: { from: Date; to: Date } | null
  weeksSummaryLoading: boolean
  workPeriodsForTable: WorkPeriodWithSummary[]
  employerRulesForTable: WeekSummary['employerRules']
  resolveEmployerRuleDay: (wp: WorkPeriodWithSummary) => SummaryDayData | undefined
  /** Edit mode is controlled by the parent so the week range clamp can be relaxed while editing. */
  isEditing: boolean
  onEditingChange: (value: boolean) => void
}

/**
 * Work periods tab: week range filter, daily table, or inline calendar editor with save/cancel.
 */
const MissionWorkPeriodsPanel: React.FC<MissionWorkPeriodsPanelProps> = ({
  mission,
  weekRangeFromIso,
  weekRangeToIso,
  onWeekRangeFromChange,
  onWeekRangeToChange,
  summaryFromTo,
  weeksSummaryLoading,
  workPeriodsForTable,
  employerRulesForTable,
  resolveEmployerRuleDay,
  isEditing,
  onEditingChange,
}) => {
  const hasCancelledEndDate =
    mission.isCancelled &&
    mission.history
      ?.find(h => h.action === 'missionCancelled')
      ?.changes?.find(c => c.attribute === 'mission.end')?.oldValue

  const canEditHoraires =
    mission.__actions.canBeUpdated && Boolean(weekRangeFromIso && weekRangeToIso && summaryFromTo)

  return (
    <Card>
      <Card.Content>
        <Level mb={4}>
          <Level.Side>
            <Heading size={5}>
              {`Du ${localDate(mission.start)} ${mission.end ? `au ${localDate(mission.end)}` : ''}`}
            </Heading>
          </Level.Side>
          <Level.Side align="right">
            <Protected roles={['employerMember']}>
              {!isEditing && canEditHoraires && (
                <Button
                  size="small"
                  onClick={() => onEditingChange(true)}
                  data-test="mission-work-periods-update"
                >
                  <Icon size="small" className="mr-2">
                    <EditIcon />
                  </Icon>
                  <span>Modifier les horaires</span>
                </Button>
              )}
            </Protected>
          </Level.Side>
        </Level>
        <Element mb={4}>
          <Level mb={2}>
            <Level.Side>
              <WeekRangeFilter
                fromValue={weekRangeFromIso}
                toValue={weekRangeToIso}
                onFromChange={onWeekRangeFromChange}
                onToChange={onWeekRangeToChange}
                minDate={isEditing ? undefined : startOfWeek(new Date(mission.start))}
                maxDate={isEditing || !mission.end ? undefined : endOfWeek(new Date(mission.end))}
                maxRangeDays={365}
              />
            </Level.Side>
          </Level>
          <Element className="buttons">
            <Button
              size="small"
              onClick={() => {
                const now = new Date()
                onWeekRangeFromChange(dateToDateInputValue(startOfWeek(now)))
                onWeekRangeToChange(dateToDateInputValue(endOfWeek(now)))
              }}
            >
              Semaine en cours
            </Button>
            <Button
              size="small"
              onClick={() => {
                const lastMonth = subMonths(new Date(), 1)
                onWeekRangeFromChange(dateToDateInputValue(startOfWeek(startOfMonth(lastMonth))))
                onWeekRangeToChange(dateToDateInputValue(endOfWeek(endOfMonth(lastMonth))))
              }}
            >
              Dernier mois
            </Button>
            {!mission.scheduleTemplate && (
              <Button
                size="small"
                onClick={() => {
                  onWeekRangeFromChange(dateToDateInputValue(mission.start))
                  onWeekRangeToChange(dateToDateInputValue(mission.end))
                }}
              >
                Toutes les journées
              </Button>
            )}
          </Element>
        </Element>
        {!summaryFromTo || weeksSummaryLoading ? (
          <Loader />
        ) : isEditing ? (
          <InlineWorkPeriodsForm
            mission={mission}
            weekRangeFromIso={dateToDateInputValue(summaryFromTo.from) as IsoDate}
            weekRangeToIso={dateToDateInputValue(summaryFromTo.to) as IsoDate}
            onCancel={() => onEditingChange(false)}
            onSuccess={() => onEditingChange(false)}
          />
        ) : (
          <>
            <DailyWorkPeriodsTable
              workPeriods={workPeriodsForTable ?? []}
              employerRules={employerRulesForTable}
              resolveEmployerRuleDay={
                employerRulesForTable.length ? resolveEmployerRuleDay : undefined
              }
              noDataMessage="Aucune journée de travail"
            />
          </>
        )}
        {hasCancelledEndDate && (
          <Element textColor="grey" textAlign={'center'}>
            Cette mission a été interrompue, elle était initialement prévue jusqu'au{' '}
            {localDate(hasCancelledEndDate)}
          </Element>
        )}
      </Card.Content>
    </Card>
  )
}

export default MissionWorkPeriodsPanel
